/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Link as TanStackLink,
  type LinkComponentProps,
  useMatch,
  useParams,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import {
  type AnchorHTMLAttributes,
  type ForwardRefExoticComponent,
  type RefAttributes,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
  forwardRef,
  useMemo,
} from 'react';
import {
  type AnyRouter,
  type BlockerFn,
  type HistoryLocation,
  createBrowserHistory,
} from '@tanstack/react-router';
import { getRouter, peekRouter } from './instance';
import { Query, parseSearch, stringifySearch } from './search';

export type Location = {
  pathname: string;
  search: string;
  hash: string;
  state: any;
  query: Query;
};

export type LocationDescriptor =
  | string
  | {
      pathname?: string;
      search?: string;
      query?: Record<string, unknown>;
      hash?: string;
      state?: any;
    };

type RouteLeaveHook = (nextLocation?: Location) => boolean | string | void;

export type PlainRoute<_T = unknown> = Record<string, unknown>;

export type InjectedRouter = {
  push: (to: LocationDescriptor) => void;
  replace: (to: LocationDescriptor) => void;
  go: (n: number) => void;
  goBack: () => void;
  goForward: () => void;
  setRouteLeaveHook: (route: unknown, hook: RouteLeaveHook) => () => void;
};

export type WithRouterProps<P = any, Q = any> = {
  router: InjectedRouter;
  location: Location & { query: Q };
  params: P;
  routes: Array<PlainRoute>;
};

export type RouteComponentProps<P = any, R = any, Q = any> = WithRouterProps<
  P,
  Q
> & {
  route: PlainRoute;
  routeParams: R;
  children?: ReactNode;
};

const basepath = () => (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');

const trimTrailingSlash = (pathname: string) =>
  pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;

const toLocation = (raw: {
  pathname: string;
  search: string;
  hash: string;
  state?: any;
}): Location => ({
  pathname: raw.pathname,
  search: raw.search,
  hash: raw.hash,
  state: raw.state,
  query: parseSearch(raw.search),
});

const toPath = (to: LocationDescriptor): string => {
  if (typeof to === 'string') return to;
  const search = to.query ? stringifySearch(to.query) : (to.search ?? '');
  return `${to.pathname ?? ''}${search}${to.hash ?? ''}`;
};

const toState = (to: LocationDescriptor) =>
  typeof to === 'string' ? undefined : to.state;

let standaloneHistory: ReturnType<typeof createBrowserHistory> | undefined;

const activeHistory = () =>
  peekRouter()?.history ?? (standaloneHistory ??= createBrowserHistory());

const currentLocation = (): Location =>
  toLocation({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    state: window.history.state,
  });

const NO_ROUTE: PlainRoute = {};
const NO_ROUTES = [NO_ROUTE];

const routesById = new Map<string, PlainRoute>();

const routeObject = (routeId: string): PlainRoute => {
  let route = routesById.get(routeId);
  if (!route) {
    route = { id: routeId };
    routesById.set(routeId, route);
  }
  return route;
};

const routeLeaveHooks = new Map<PlainRoute, RouteLeaveHook>();

const withoutBasepath = (pathname: string) => {
  const base = basepath();
  return base && pathname.startsWith(base)
    ? pathname.slice(base.length) || '/'
    : pathname;
};

const askToLeave = (hook: RouteLeaveHook, next: Location) => {
  const result = hook(next);
  if (typeof result === 'string') return !window.confirm(result);
  return result === false;
};

type LeaveBlocker = { router: AnyRouter; remove: () => void };

let leaveBlocker: LeaveBlocker | null = null;

const isLeaveBlocked = (
  router: AnyRouter,
  nextLocation: { pathname: string; search: string; hash: string; state?: any },
) => {
  const next = toLocation({
    ...nextLocation,
    pathname: withoutBasepath(nextLocation.pathname),
  });
  // As in react-router 3, a route also leaves when its own params change.
  const matchKey = (match: { routeId: string; pathname: string }) =>
    `${match.routeId}\0${match.pathname.replace(/(.)\/$/, '$1')}`;
  const nextKeys = new Set(router.matchRoutes(next.pathname, {}).map(matchKey));
  const staying = new Set(
    router.state.matches
      .filter((match: { routeId: string; pathname: string }) =>
        nextKeys.has(matchKey(match)),
      )
      .map((match: { routeId: string }) => match.routeId),
  );
  return Array.from(routeLeaveHooks).some(
    ([route, hook]) =>
      !staying.has(route.id as string) && askToLeave(hook, next),
  );
};

const removeLeaveBlocker = () => {
  leaveBlocker?.remove();
  leaveBlocker = null;
};

// TanStack awaits every blocker before a PUSH or REPLACE, which would defer
// the URL change past history.flush(). Keep the blocker only while a hook needs it.
const syncLeaveBlocker = () => {
  if (!routeLeaveHooks.size) return removeLeaveBlocker();
  const router = getRouter();
  if (leaveBlocker?.router === router) return;
  removeLeaveBlocker();
  const removeBlock = router.history.block({
    enableBeforeUnload: () =>
      Array.from(routeLeaveHooks.values()).some(
        (hook) => typeof hook() === 'string',
      ),
    blockerFn: ({ nextLocation }: Parameters<BlockerFn>[0]) =>
      isLeaveBlocked(router, nextLocation),
  });
  const unsubscribe = router.subscribe('onResolved', () => {
    const active = new Set(
      router.state.matches.map((match: { routeId: string }) => match.routeId),
    );
    routeLeaveHooks.forEach((_hook, route) => {
      if (!active.has(route.id as string)) routeLeaveHooks.delete(route);
    });
    syncLeaveBlocker();
  });
  leaveBlocker = {
    router,
    remove: () => {
      removeBlock();
      unsubscribe();
    },
  };
};

// push and replace answer the leave hooks themselves and bypass the blocker,
// so the URL changes before they return even while a hook is registered.
const leaveAllowed = (path: string) => {
  if (!leaveBlocker) return true;
  const { pathname, search, hash } = new URL(path, 'http://router.local');
  return !isLeaveBlocked(leaveBlocker.router, { pathname, search, hash });
};

const isCurrent = (history: ReturnType<typeof activeHistory>, path: string) =>
  history.location.href === path;

const navigateNow = (
  history: ReturnType<typeof activeHistory>,
  method: 'push' | 'replace',
  path: string,
  state: any,
) => {
  if (!leaveAllowed(path)) return;
  history[method](path, state, { ignoreBlocker: true });
  history.flush();
};

const pushOrReplace = (
  history: ReturnType<typeof activeHistory>,
  to: LocationDescriptor,
  base = '',
) => {
  const path = base + toPath(to);
  navigateNow(
    history,
    isCurrent(history, path) ? 'replace' : 'push',
    path,
    toState(to),
  );
};

export const routerCompat: InjectedRouter = {
  push: (to) => pushOrReplace(getRouter().history, to, basepath()),
  replace: (to) =>
    navigateNow(
      getRouter().history,
      'replace',
      basepath() + toPath(to),
      toState(to),
    ),
  go: (n) => getRouter().history.go(n),
  goBack: () => getRouter().history.back(),
  goForward: () => getRouter().history.forward(),
  setRouteLeaveHook: (route, hook) => {
    const key = (route ?? NO_ROUTE) as PlainRoute;
    routeLeaveHooks.set(key, hook);
    syncLeaveBlocker();
    return () => {
      if (routeLeaveHooks.get(key) !== hook) return;
      routeLeaveHooks.delete(key);
      syncLeaveBlocker();
    };
  },
};

export const browserHistory = {
  push: (to: LocationDescriptor) => pushOrReplace(activeHistory(), to),
  replace: (to: LocationDescriptor) =>
    navigateNow(activeHistory(), 'replace', toPath(to), toState(to)),
  go: (n: number) => activeHistory().go(n),
  goBack: () => activeHistory().back(),
  getCurrentLocation: currentLocation,
  listen: (listener: (location: Location) => void) => {
    const history = activeHistory();
    return history.subscribe(({ location }: { location: HistoryLocation }) => {
      history.flush();
      listener(toLocation(location));
    });
  },
};

const wrapped = new WeakMap<ComponentType<any>, ComponentType<any>>();

export function withRouter<P extends object>(
  Component: ComponentType<P>,
): ComponentType<Omit<P, keyof WithRouterProps> & Partial<WithRouterProps>> {
  const existing = wrapped.get(Component);
  if (existing) return existing;

  const Connected = (props: any) => {
    // resolvedLocation keeps a page on its own URL while the next route loads,
    // but TanStack updates it only after the next page has rendered. Once the
    // committed matches are ahead of it, the page is already the next one and
    // reads the current location, so its first render sees its own query.
    const raw = useRouterState({
      select: (state) => {
        const resolved = state.resolvedLocation;
        const leaf = state.matches[state.matches.length - 1];
        return resolved &&
          leaf &&
          trimTrailingSlash(leaf.pathname) ===
            trimTrailingSlash(resolved.pathname)
          ? resolved
          : state.location;
      },
    });
    const params = useParams({ strict: false });
    const routeId = useMatch({
      strict: false,
      select: (match) => match.routeId,
    });
    const location = useMemo(
      () =>
        toLocation({
          pathname: raw.pathname,
          search: raw.searchStr,
          hash: raw.hash ? `#${raw.hash}` : '',
          state: raw.state,
        }),
      [raw.pathname, raw.searchStr, raw.hash, raw.state],
    );
    const matchedRouteIds = useRouterState({
      select: (state) =>
        state.matches
          .map((match: { routeId: string }) => match.routeId)
          .join('|'),
    });
    const route = routeObject(routeId ?? '');
    const routes = useMemo(
      () => matchedRouteIds.split('|').map(routeObject),
      [matchedRouteIds],
    );
    return (
      <Component
        {...props}
        router={routerCompat}
        location={location}
        params={params}
        routeParams={params}
        routes={routes}
        route={route}
      />
    );
  };
  const WithRouter = (props: any) =>
    useRouter({ warn: false }) ? (
      <Connected {...props} />
    ) : (
      <Component
        {...props}
        router={routerCompat}
        location={currentLocation()}
        params={{}}
        routeParams={{}}
        routes={NO_ROUTES}
        route={NO_ROUTE}
      />
    );
  if (Component) wrapped.set(Component, WithRouter);
  return WithRouter;
}

export type LinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
> & {
  to?: LocationDescriptor;
  href?: string;
  activeClassName?: string;
  activeStyle?: CSSProperties;
  onlyActiveOnIndex?: boolean;
};

export type IndexLinkProps = LinkProps;

type NavigationLinkProps = Omit<
  LinkComponentProps<'a', AnyRouter, string, string>,
  'search'
> & { search: Query };

// The non-strict tsconfig makes TanStack's inferred search type never.
const NavigationLink = TanStackLink as unknown as ForwardRefExoticComponent<
  NavigationLinkProps & RefAttributes<HTMLAnchorElement>
>;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      to,
      href,
      activeClassName,
      activeStyle,
      onlyActiveOnIndex,
      children,
      ...anchorProps
    },
    ref,
  ) => {
    const hasRouter = useRouter({ warn: false });
    const path = to ? toPath(to) : href;
    const isAbsolute = path && /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(path);
    if (!to || !hasRouter || isAbsolute) {
      return (
        <a ref={ref} {...anchorProps} href={path}>
          {children}
        </a>
      );
    }
    const target = new URL(toPath(to), 'http://router.local');
    return (
      <NavigationLink
        ref={ref}
        to={target.pathname}
        search={parseSearch(target.search)}
        hash={target.hash.slice(1) || undefined}
        state={toState(to)}
        activeProps={{ className: activeClassName, style: activeStyle }}
        activeOptions={{
          exact: onlyActiveOnIndex,
          includeSearch: !onlyActiveOnIndex,
        }}
        {...anchorProps}
      >
        {children}
      </NavigationLink>
    );
  },
);
Link.displayName = 'Link';

export const IndexLink = forwardRef<HTMLAnchorElement, LinkProps>(
  (props, ref) => <Link ref={ref} {...props} onlyActiveOnIndex />,
);
IndexLink.displayName = 'IndexLink';
